import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useAxios from 'axios-hooks';
import { format, parse } from 'date-fns';
import isString from 'lodash.isstring';
import uniq from 'lodash.uniq';
import ClickToEdit from 'react-click-to-edit';
import isEmpty from 'lodash.isempty';
import isNumber from 'lodash.isnumber';
import ReactTooltip from 'react-tooltip';
import { Link } from 'react-router-dom';

import {
	Checkbox,
	Button,
	Icon,
	DropdownButton,
	SplitButton,
} from '../../../components';
import { short } from '../common/formatting/date';
import PCPFilter from './components/PCPFilter';
import QuestionnaireFilter from './components/QuestionnaireFilter';
import PatientNameFilter from './components/PatientNameFilter';
import QuestionnaireResultsModal from './QuestionnaireResultsModal';
import CampaignPatientModal from './components/CampaignPatientModal';
import SendProgressModal from './components/SendProgressModal';
import ViewQuestionnaireModal from './components/ViewQuestionnaireModal';
import QuestionnaireFormModal from './components/QuestionnaireFormModal';
import ACRSPatientSearchModal from './components/ACRSPatientSearchModal';
import { isNumberMobile } from '../tracking-board/api';
import './styles.scss';

const DEFAULT_FILTER = {
	status: null,
	pcp_npi: null,
	patient_first_name: null,
	patient_last_name: null,
	patient_dob: null,
	campaigns: null,
	offset: 0,
	record_limit: 10,
};

const getDOBString = (birthDate) => {
	if (isString(birthDate)) {
		const [dt] = birthDate.split('T');
		if (dt) {
			return format(parse(dt, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy');
		}
	}
	return '';
};

const Campaigns = ({ user, showNotification }) => {
	const [selectedPCPs, setSelectedPCPs] = useState([]);
	const [selectedCampaigns, setSelectedCampaigns] = useState([]);
	const [selectedStatus, setSelectedStatus] = useState(null);
	const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
	const [showCallQueueOnly, setShowCallQueueOnly] = useState(false);
	const [selectedPatients, setSelectedPatients] = useState([]);
	const [offset, setOffset] = useState(0);
	const [updating, setUpdating] = useState(false);
	const [isResultsModalOpen, setResultsModalOpen] = useState(false);
	const [selectedCampaignSentId, setSelectedCampaignSentId] = useState(null);
	const [patientSearchData, setPatientSearchData] = useState({});
	const [isCampaignPatientModalOpen, setCampaignPatientModalOpen] =
		useState(false);
	const [phoneData, setPhoneData] = useState();
	const [campaignPatient, setCampaignPatient] = useState(null);
	const [isSending, setIsSending] = useState(false);
	const [sendsCompleted, setSendsCompleted] = useState(0);
	const [isViewingQuestionnaire, setIsViewingQuestionnaire] = useState(false);
	const [isViewingQuestionnaireForm, setIsViewingQuestionnaireForm] =
		useState(false);
	const [isACRSPatientSearchModalOpen, setACRSPatientSearchModalOpen] =
		useState(false);

	const [{ data, loading /* error */ }, getCampaignList] = useAxios(
		{
			method: 'GET',
			url: `/providers/${user.get('id')}/campaign-list`,
		},
		{ manual: true, autoCancel: false }
	);
	const [{ data: optionsData /* loading: loadingOptions, error */ }] = useAxios(
		`/providers/${user.get('id')}/campaign-filter-option` /* { manual: true } */
	);
	const [, /* { loading: updating, error } */ updateCampaignItem] = useAxios(
		{
			url: `/providers/${user.get('id')}/campaign-update`,
			method: 'POST',
		},
		{ manual: true, autoCancel: false }
	);
	const [{ loading: sending /* error: sendError */ }, sendCampaignText] =
		useAxios(
			{
				url: `/providers/${user.get('id')}/campaign-text`,
				method: 'POST',
			},
			{ manual: true, autoCancel: false }
		);
	const [
		{ loading: isAddingOrUpdatingPatient /* error: sendError */ },
		addOrUpdatePatient,
	] = useAxios(
		{
			url: `/providers/${user.get('id')}/campaign-add`,
			method: 'POST',
		},
		{ manual: true }
	);

	useEffect(() => {
		setOffset(0);
	}, [patientSearchData]);

	useEffect(() => {
		const params = {
			...DEFAULT_FILTER,
			...patientSearchData,
			offset,
			status: selectedStatus,
		};
		if (showFlaggedOnly) {
			params.flag_ind = showFlaggedOnly;
		}
		if (showCallQueueOnly) {
			params.call_queue_ind = showCallQueueOnly;
		}
		if (selectedPCPs.length > 0) {
			params.pcp_npi = selectedPCPs; // .map(({ pcp_npi }) => pcp_npi);
		}
		if (Array.isArray(selectedCampaigns) && selectedCampaigns.length > 0) {
			params.campaigns = selectedCampaigns;
		}
		if (!sending && !updating) {
			getCampaignList({ params });
		}
	}, [
		selectedStatus,
		showFlaggedOnly,
		showCallQueueOnly,
		selectedPCPs,
		selectedCampaigns,
		offset,
		sending,
		updating,
		patientSearchData,
	]);

	useEffect(() => {
		if (!isAddingOrUpdatingPatient) {
			setCampaignPatientModalOpen(false);
			setCampaignPatient(null);
		}
	}, [isAddingOrUpdatingPatient]);

	const handlePatientSelected = ({ target }) => {
		if (target.checked) {
			setSelectedPatients(uniq([...selectedPatients, target.value]));
		} else {
			setSelectedPatients(
				selectedPatients.filter((rowId) => rowId !== target.value)
			);
		}
	};

	const handleShowAll = () => {
		setSelectedStatus(null);
		setShowFlaggedOnly(false);
		setShowCallQueueOnly(false);
		setOffset(0);
	};

	const handleShowStatus = ({ target }) => {
		setSelectedStatus(target.name);
		setShowFlaggedOnly(false);
		setShowCallQueueOnly(false);
		setOffset(0);
	};

	const handleShowFlaggedOnly = () => {
		setSelectedStatus(null);
		setShowFlaggedOnly(true);
		setShowCallQueueOnly(false);
		setOffset(0);
	};

	const handleShowCallQueueOnly = () => {
		setSelectedStatus(null);
		setShowFlaggedOnly(false);
		setShowCallQueueOnly(true);
		setOffset(0);
	};

	const handleMoveToCallQueue = async () => {
		setUpdating(true);
		for (let i = 0; i < selectedPatients.length; i++) {
			await updateCampaignItem({
				data: {
					campaign_demographic_id: parseInt(selectedPatients[i], 10),
					call_queue_ind: true,
				},
			});
		}
		setSelectedPatients([]);
		setUpdating(false);
	};

	const handleToggleFlagImportant = async () => {
		setUpdating(true);
		for (let i = 0; i < selectedPatients.length; i++) {
			let flag_ind = true;
			const campaignDemographicId = parseInt(selectedPatients[i], 10);
			const campaignPatient = data.find(
				(row) => row.campaign_demographic_id === campaignDemographicId
			);
			if (campaignPatient && Boolean(campaignPatient.flagged_ind)) {
				flag_ind = false;
			}
			await updateCampaignItem({
				data: {
					campaign_demographic_id: campaignDemographicId,
					flag_ind,
				},
			});
		}
		setSelectedPatients([]);
		setUpdating(false);
	};

	const handleUpdateNote = async (campaign_demographic_id, note) => {
		await updateCampaignItem({
			data: {
				campaign_demographic_id,
				note,
			},
		});
	};

	const handleNextClick = () => {
		const newOffset = offset + DEFAULT_FILTER.record_limit;
		setOffset(newOffset);
	};

	const handlePreviousClick = () => {
		let newOffset = offset - DEFAULT_FILTER.record_limit;
		if (newOffset < 0) {
			newOffset = 0;
		}
		setOffset(newOffset);
	};

	const handleSetSelectedPCPs = (selectedPCPs) => {
		setSelectedPCPs(selectedPCPs);
		setOffset(0);
	};

	const handleSetSelectedCampaigns = (selectedCampaigns) => {
		setSelectedCampaigns(selectedCampaigns);
		setOffset(0);
	};

	const handleSend = () => {
		setSendsCompleted(0);
		setIsSending(true);
		selectedPatients.map(async (campaign_demographic_id, index) => {
			await sendCampaignText({
				data: {
					campaign_demographic_id: parseInt(campaign_demographic_id, 10),
				},
			});
			setSendsCompleted(index + 1);
			if (index === selectedPatients.length - 1) {
				setSendsCompleted(selectedPatients.length);
				setTimeout(() => {
					setIsSending(false);
					setSelectedPatients([]);
					setSendsCompleted(0);
				}, 500);
			}
		});
	};

	const handleOpenQuestionnaireResultsModal = (campaignSentId) => {
		setSelectedCampaignSentId(campaignSentId);
		setResultsModalOpen(true);
		const campaignPatient = data.find(
			({ campaign_sent_id }) => campaign_sent_id === campaignSentId
		);
		setCampaignPatient(campaignPatient);
	};

	const handleOpenCampaignPatientModal = (patientId) => {
		if (isNumber(patientId)) {
			const campaignPatient = data.find(
				({ campaign_demographic_id }) => campaign_demographic_id === patientId
			);
			if (campaignPatient) {
				setCampaignPatient(campaignPatient);
			}
		} else {
			setCampaignPatient(null);
		}
		setCampaignPatientModalOpen(true);
	};

	const handleVerifyMobileNumber = async (number) => {
		const userId = user.get('id');
		const phoneData = await isNumberMobile(userId, number);
		setPhoneData(phoneData);
	};

	const handleAddOrUpdatePatient = async (data, sendToDocument) => {
		setUpdating(true);
		let addedPatient;
		try {
			({ data: addedPatient } = await addOrUpdatePatient({ data }));
		} catch (err) {
			showNotification({
				message: 'Unable to add campaign patient!',
				status: 'danger',
				timeout: 10000,
			});
		}
		setUpdating(false);
		if (sendToDocument && addedPatient) {
			setCampaignPatient({
				...addedPatient,
				campaign_demographic_id: addedPatient.id,
			});
			setIsViewingQuestionnaireForm(true);
		}
	};

	const handleOpenQuestionnaireFormModal = (patientId) => {
		if (isNumber(patientId)) {
			const campaignPatient = data.find(
				({ campaign_demographic_id }) => campaign_demographic_id == patientId
			);
			if (campaignPatient) {
				setCampaignPatient(campaignPatient);
			}
		} else {
			setCampaignPatient(null);
		}
		setIsViewingQuestionnaireForm(true);
	};

	const handleAddACRSPatientToCampaign = (patient) => {
		setACRSPatientSearchModalOpen(false);
		let birth_dt;
		if (!isEmpty(patient.patient_date_of_birth)) {
			const [month, day, year] = patient.patient_date_of_birth.split('/');
			birth_dt = `${year}-${month}-${day}`;
		}
		setCampaignPatient({
			gender: patient.gender,
			city: patient.patient_city,
			address1: patient.patient_address_1,
			address2: patient.patient_address_2,
			first_name: patient.patient_first_name,
			last_name: patient.patient_last_name,
			phone_mobile: patient.patient_phone_dash_mobile,
			state: patient.patient_state,
			zip: patient.patient_zip,
			birth_dt,
			acrs_patient_id: patient.unique_patient_id,
		});
		setCampaignPatientModalOpen(true);
	};

	const startingAt = offset + 1;
	let endingAt;
	if (data) {
		endingAt = offset + data.length;
	}

	return (
		<div className="content-wrapper campaigns">
			<QuestionnaireResultsModal
				isOpen={isResultsModalOpen}
				onClose={() => setResultsModalOpen(false)}
				providerId={user.get('id')}
				campaign_sent_id={selectedCampaignSentId}
				campaignPatient={campaignPatient}
			/>
			<CampaignPatientModal
				isOpen={isCampaignPatientModalOpen}
				onClose={() => setCampaignPatientModalOpen(false)}
				onAdd={handleAddOrUpdatePatient}
				isAdding={isAddingOrUpdatingPatient}
				onVerifyMobileNumber={handleVerifyMobileNumber}
				phoneData={phoneData}
				userId={user.get('id')}
				campaignPatient={campaignPatient}
			/>
			<SendProgressModal
				isOpen={isSending}
				totalSent={sendsCompleted}
				totalToSend={selectedPatients.length}
			/>
			<ViewQuestionnaireModal
				isOpen={isViewingQuestionnaire}
				onClose={() => setIsViewingQuestionnaire(false)}
				careQuestGroupId={
					Array.isArray(selectedCampaigns) &&
					selectedCampaigns.length > 0 &&
					selectedCampaigns[0]
				}
			/>
			<QuestionnaireFormModal
				isOpen={isViewingQuestionnaireForm}
				onClose={() => setIsViewingQuestionnaireForm(false)}
				userId={user.get('id')}
				campaignPatient={campaignPatient}
			/>
			<ACRSPatientSearchModal
				isOpen={isACRSPatientSearchModalOpen}
				onClose={() => setACRSPatientSearchModalOpen(false)}
				providerId={user.get('id')}
				onAddToCampaign={handleAddACRSPatientToCampaign}
			/>
			<ol className="breadcrumb">
				<li>
					<Link to="/">Home</Link>
				</li>
				<li className="active">Outreach Campaigns</li>
			</ol>
			<div className="table-grid table-grid-desktop">
				<div className="col col-md">
					<div className="pr">
						<div className="mb-boxes collapse">
							<PCPFilter
								pcpOptions={optionsData && optionsData.pcp_list}
								onSelectedPCPsChanged={handleSetSelectedPCPs}
							/>
							<PatientNameFilter
								onDataChange={(data) => setPatientSearchData(data)}
							/>
							<QuestionnaireFilter
								questionnaireOptions={optionsData && optionsData.campaign_list}
								onSelectedQuestionnairesChanged={handleSetSelectedCampaigns}
								selectedQuestionnaires={selectedCampaigns}
							/>
							<div className="panel panel-primary">
								<div className="panel-heading">Campaign View Status</div>
								<div className="panel-body">
									<Button
										title="All"
										onClick={handleShowAll}
										wide
										primary={
											selectedStatus === null &&
											!showFlaggedOnly &&
											!showCallQueueOnly
										}
									/>
									<Button
										title="Unsent"
										onClick={handleShowStatus}
										name="Unsent"
										wide
										primary={selectedStatus === 'Unsent'}
									/>
									<Button
										title="Sent"
										onClick={handleShowStatus}
										name="Sent"
										wide
										primary={selectedStatus === 'Sent'}
									/>
									<Button
										title="Invite Opened"
										onClick={handleShowStatus}
										name="Pending"
										wide
										primary={selectedStatus === 'Pending'}
									/>
									<Button
										title="Auth Failed"
										onClick={handleShowStatus}
										name="Action Needed"
										wide
										primary={selectedStatus === 'Action Needed'}
									/>
									<Button
										title="Completed"
										onClick={handleShowStatus}
										name="Completed"
										wide
										primary={selectedStatus === 'Completed'}
									/>
									<Button
										title="Flagged Items"
										onClick={handleShowFlaggedOnly}
										wide
										primary={selectedStatus === null && showFlaggedOnly}
									/>
									<Button
										title="Call Queue"
										onClick={handleShowCallQueueOnly}
										wide
										primary={selectedStatus === null && showCallQueueOnly}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col">
					<div className="table-responsive">
						<div>
							<Button
								title="Send"
								rightIcon="send"
								primary
								disabled={updating || sending || selectedPatients.length < 1}
								onClick={handleSend}
							/>
							{/* <Button title="Mark as Completed" rightIcon="envelope" primary className="ml" disabled={selectedPatients.length < 1} onClick={() => {}} /> */}
							<Button
								title="Flag Important"
								rightIcon="flag"
								primary
								className="ml"
								disabled={updating || sending || selectedPatients.length < 1}
								onClick={handleToggleFlagImportant}
							/>
							<Button
								title="Move to Call Queue"
								rightIcon="mobile"
								primary
								className="ml"
								disabled={updating || sending || selectedPatients.length < 1}
								onClick={handleMoveToCallQueue}
							/>
							<Button
								title="View Questionnaire"
								rightIcon="list-ul"
								primary
								className="ml"
								disabled={selectedCampaigns.length != 1}
								onClick={() => setIsViewingQuestionnaire(true)}
							/>
							{/* <Button title="Complete Questionnaire" rightIcon="check" primary className="ml" disabled={selectedPatients.length != 1} onClick={handleOpenQuestionnaireFormModal} /> */}
						</div>
						<div className="panel panel-default mt">
							<div className="panel-heading">
								{loading ? (
									<span>Loading...</span>
								) : (
									<React.Fragment>
										<div className="pull-right">
											{/* <Button
												title="Add Patient"
												onClick={handleOpenCampaignPatientModal}
												primary
												leftIcon="plus"
											/> */}
											<SplitButton
												primary
												onClick={handleOpenCampaignPatientModal}
												title="Add Patient"
												right
												items={[
													{
														title: 'Add Patient from ACRS',
														onClick: () => setACRSPatientSearchModalOpen(true),
													},
												]}
											/>
										</div>
										<div>
											Showing {startingAt} - {endingAt}
											<Button
												icon="chevron-left"
												onClick={handlePreviousClick}
												disabled={offset < 1}
											/>
											<Button
												icon="chevron-right"
												onClick={handleNextClick}
												disabled={
													data && data.length < DEFAULT_FILTER.record_limit
												}
											/>
										</div>
									</React.Fragment>
								)}
							</div>
							<div className="panel-body">
								<table className="table table-striped table-hover">
									<thead>
										<tr>
											<th colSpan={3}>Patient</th>
											<th>Gender</th>
											<th>DOB</th>
											<th>Phone</th>
											<th>Email</th>
											<th>Status</th>
											<th>Sent</th>
											<th>Completed</th>
											<th>Notes</th>
											<th>Campaign(s)</th>
										</tr>
									</thead>
									<tbody>
										{loading && (
											<tr>
												<td colSpan={12}>
													<em className="fa fa-spin fa-circle-o-notch" />
												</td>
											</tr>
										)}
										{!loading && data && data.length === 0 && (
											<tr>
												<td colSpan={12}>No records found</td>
											</tr>
										)}
										{!loading &&
											data &&
											data.map(
												({ number_sent, number_completed = 0, ...patient }) => (
													<tr key={`${patient.campaign_demographic_id}`}>
														<td>
															<Checkbox
																onChange={handlePatientSelected}
																checked={selectedPatients.includes(
																	`${patient.campaign_demographic_id}`
																)}
																value={patient.campaign_demographic_id}
															/>
														</td>
														<td>
															<a
																data-for={`patient-${patient.campaign_demographic_id}`}
																data-tip
															>
																{patient.first_name} {patient.middle_name}{' '}
																{patient.last_name}
															</a>
															<ReactTooltip
																place="right"
																type="dark"
																effect="solid"
																id={`patient-${patient.campaign_demographic_id}`}
															>
																<div>
																	{patient.address1}
																	<br />
																	{!isEmpty(patient.address2) && (
																		<React.Fragment>
																			{patient.address2}
																			<br />
																		</React.Fragment>
																	)}
																	{patient.city} {patient.state} {patient.zip}
																</div>
															</ReactTooltip>
														</td>
														<td className="text-primary">
															{Boolean(patient.flagged_ind) && (
																<Icon name="fa-flag" />
															)}
														</td>
														<td>{patient.gender}</td>
														<td>{getDOBString(patient.birth_dt)}</td>
														<td>{patient.phone_mobile}</td>
														<td>{patient.email}</td>
														<td>
															{patient.status === 'Pending'
																? 'Invite Opened'
																: patient.status}
														</td>
														<td>
															{patient.sent_dttm && short(patient.sent_dttm)}
														</td>
														<td>
															{number_sent > 0 &&
																number_sent === number_completed &&
																short(patient.last_updated_dttm)}
														</td>
														<td>
															<ClickToEdit
																initialValue={
																	isEmpty(patient.note)
																		? 'Insert Notes Here'
																		: patient.note
																}
																endEditing={(note) =>
																	handleUpdateNote(
																		patient.campaign_demographic_id,
																		note
																	)
																}
																textClass="note-static"
															/>
														</td>
														<td className="text-right">
															{isNumber(number_completed) &&
															number_completed > 0 ? (
																<Button
																	primary
																	title="View Results"
																	onClick={() =>
																		handleOpenQuestionnaireResultsModal(
																			patient.campaign_sent_id
																		)
																	}
																/>
															) : patient.status === 'Unsent' ? (
																<DropdownButton
																	primary
																	split
																	right
																	titleDefault="Edit"
																	onClickDefault={() =>
																		handleOpenCampaignPatientModal(
																			patient.campaign_demographic_id
																		)
																	}
																	items={[
																		{
																			title: 'Complete Questionnaire',
																			onClick: () =>
																				handleOpenQuestionnaireFormModal(
																					patient.campaign_demographic_id
																				),
																		},
																	]}
																/>
															) : (
																<Button
																	title="Edit"
																	onClick={() =>
																		handleOpenCampaignPatientModal(
																			patient.campaign_demographic_id
																		)
																	}
																	primary
																/>
															)}
														</td>
													</tr>
												)
											)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

Campaigns.propTypes = {
	user: PropTypes.object,
	showNotification: PropTypes.func,
};

export default Campaigns;
